import {
  SHChip,
  SHImage,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { GlobalProductsToHideSubProductName } from "@constants/products";
import { PortfolioCompositionSetupDTO } from "@models/platform-analysis/entities/steps/summary";
import { Collapse } from "@mui/material";
import { toSentence } from "@utils";
import { isNil } from "lodash";
import { memo, useMemo } from "react";
import { getProductRecommendationConfigs } from "../../../../utils";

interface SummaryPortfolioCompositionSetupLogoProps {
  setup: PortfolioCompositionSetupDTO;
}

export const SummaryPortfolioCompositionSetupLogo = memo(
  ({ setup }: SummaryPortfolioCompositionSetupLogoProps) => {
    const shouldHideSetupName = useMemo(() => {
      return GlobalProductsToHideSubProductName.includes(
        setup.productName || "",
      );
    }, [setup.productName]);

    const renderRecommendationBadge = () => {
      const { backgroundColor, icon, isCustomType, label } =
        getProductRecommendationConfigs(setup.recommendation);

      return (
        <Collapse
          in={!isNil(label)}
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SHStack
            height={20}
            bgcolor={backgroundColor || "transparent"}
            width={"100%"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <SHStack
              height={20}
              direction={"row"}
              alignItems={"center"}
              gap={0.5}
              justifyContent={"center"}
            >
              {icon}
              <SHTypography
                variant="body3"
                colorVariant="white"
                fontSize="12px"
                sx={{ paddingTop: 0.5 }}
              >
                {isCustomType ? label : toSentence(label)}
              </SHTypography>
            </SHStack>
          </SHStack>
        </Collapse>
      );
    };

    return (
      <SHStack
        height={"100%"}
        direction={"column"}
        alignItems={"start"}
        justifyContent={"space-between"}
        gap={0.5}
        paddingTop={1}
      >
        <SHStack
          width={"100%"}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <SHStack
            direction={"column"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"15px"}
          >
            {setup.isProductMain && (
              <SHChip
                label={"Main"}
                color={"warning"}
                variant={"outlined"}
                sx={{
                  top: 5.5,
                  right: 5.5,
                  width: 51,
                  height: 22,
                  padding: "4px 8px",
                  position: "absolute",
                  "& >.MuiChip-label": {
                    overflow: "unset",
                  },
                }}
              />
            )}

            <SHStack
              direction={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <SHImage src={setup?.logo} width={50} height={50} />

              <SHTypography
                variant="subtitle1"
                textAlign={"center"}
                fontWeight={700}
                fontSize={15}
                lineHeight={1.2}
                paddingX={"1px"}
              >
                {setup?.productName}
              </SHTypography>

              {!shouldHideSetupName && setup?.name && (
                <SHTypography
                  addPaddingTop={false}
                  variant="subtitle1"
                  textAlign={"center"}
                  fontWeight={700}
                  fontSize={15}
                  lineHeight={1.2}
                >
                  {setup?.name}
                </SHTypography>
              )}
            </SHStack>
          </SHStack>
        </SHStack>

        {renderRecommendationBadge()}
      </SHStack>
    );
  },
);
